/**
 * V2 builtin registrations — the single place that binds each SectionKind to its
 * component, supported layouts, default layout, and content capabilities. The registry
 * is the only source of truth for layout validation + capabilities (CONTRACTS_FROZEN.md §6);
 * nothing else hardcodes layout lists. Replaces the V1 21-variant `registerBuiltins`.
 */
import { createSectionRegistry } from '../runtime/registry'
import type { SectionRegistry } from '../runtime/types'
import { Navbar } from './navbar/Navbar'
import { Hero } from './hero/Hero'
import { About } from './about/About'
import { Services } from './services/Services'
import { Gallery } from './gallery/Gallery'
import { Reviews } from './reviews/Reviews'
import { Faq } from './faq/Faq'
import { Contact } from './contact/Contact'
import { Footer } from './footer/Footer'

/** Register the ten builtin section kinds into a registry. */
export function registerBuiltins(registry: SectionRegistry): SectionRegistry {
  registry.register('navbar', {
    component: Navbar,
    // The Header layout is fixed to `standard` (simplification: no logo, no layout choice
    // — see Navbar.tsx / NavbarEditorPanel.tsx). `minimal`/`centered` remain valid
    // `NavbarLayout` schema values for backward compatibility with old data, but are no
    // longer offered — SectionRenderer falls back to `defaultLayout` for any section that
    // still carries one.
    supportedLayouts: ['standard'],
    defaultLayout: 'standard',
    contentCapabilities: {
      eyebrow: false,
      heading: false,
      subheading: false,
      media: false,
      items: true,
      actions: true,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('hero', {
    component: Hero,
    supportedLayouts: ['cover', 'coverCard', 'split', 'media'],
    defaultLayout: 'cover',
    contentCapabilities: {
      eyebrow: true,
      heading: true,
      subheading: true,
      media: true,
      items: false,
      actions: true,
      contact: false,
      location: false,
      social: false,
      metadata: ['overlayOpacity'],
    },
  })

  registry.register('about', {
    component: About,
    supportedLayouts: ['card', 'cardRight', 'cover'],
    defaultLayout: 'card',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: true,
      media: true,
      items: true,
      actions: false,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('services', {
    component: Services,
    supportedLayouts: ['cards', 'compact', 'icons'],
    defaultLayout: 'cards',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: false,
      media: false,
      items: true,
      actions: false,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('gallery', {
    component: Gallery,
    supportedLayouts: ['grid', 'masonry'],
    defaultLayout: 'grid',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: false,
      media: true,
      items: false,
      actions: false,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('reviews', {
    component: Reviews,
    supportedLayouts: ['columns1', 'columns2', 'columns3', 'columns4', 'carousel'],
    defaultLayout: 'columns3',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: false,
      media: false,
      items: true,
      actions: false,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('faq', {
    component: Faq,
    supportedLayouts: ['accordion', 'cards', 'minimal'],
    defaultLayout: 'accordion',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: false,
      media: false,
      items: true,
      actions: false,
      contact: false,
      location: false,
      social: false,
      metadata: [],
    },
  })

  registry.register('contact', {
    component: Contact,
    supportedLayouts: ['standard', 'split', 'premium', 'mapFocus'],
    defaultLayout: 'standard',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: true,
      media: false,
      items: true,
      actions: true,
      contact: true,
      location: true,
      social: false,
      metadata: [],
    },
  })

  registry.register('footer', {
    component: Footer,
    // The Footer has exactly one supported layout (simplification: no layout choice — see
    // FooterEditorPanel.tsx). `extended`/`minimal` remain valid `FooterLayout` schema
    // values for backward compatibility with old data, but are no longer offered —
    // SectionRenderer falls back to `defaultLayout` for any section that still carries one.
    supportedLayouts: ['default'],
    defaultLayout: 'default',
    contentCapabilities: {
      eyebrow: false,
      heading: true,
      subheading: true,
      media: false,
      items: true,
      actions: false,
      contact: false,
      location: false,
      social: true,
      metadata: [],
    },
  })

  return registry
}

/** Convenience: a fresh registry with all builtins registered. */
export function createBuilderRegistry(): SectionRegistry {
  return registerBuiltins(createSectionRegistry())
}
